import * as React from 'react'
import { useId, type ComponentProps } from 'react'

import * as SelectPrimitive from '@radix-ui/react-select'
import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react'
import { type Control, type FieldValues, type UseControllerProps } from 'react-hook-form'

import { Icon } from '@/shared/components/icon'
import { FormControl, FormField, FormItem, FormLabel } from '@/shared/components/ui/form'
import { cn } from '@/shared/lib'

function Select({ ...props }: Readonly<React.ComponentProps<typeof SelectPrimitive.Root>>) {
  return <SelectPrimitive.Root data-slot="select" {...props} />
}

function SelectGroup({ ...props }: React.ComponentProps<typeof SelectPrimitive.Group>) {
  return <SelectPrimitive.Group data-slot="select-group" {...props} />
}

function SelectValue({ ...props }: React.ComponentProps<typeof SelectPrimitive.Value>) {
  return <SelectPrimitive.Value data-slot="select-value" {...props} />
}

function SelectTrigger({
  className,
  children,
  isLoading = false,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Trigger> & { isLoading?: boolean }) {
  return (
    <SelectPrimitive.Trigger
      data-slot="select-trigger"
      className={cn(
        "border-input [&_svg:not([class*='text-'])]:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive text-md flex h-10 w-fit items-center justify-between gap-2 rounded-md border bg-neutral-50 bg-slate-100 px-3 py-2 text-base whitespace-nowrap shadow-xs transition-[color,box-shadow] outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 *:data-[slot=select-value]:line-clamp-1 *:data-[slot=select-value]:flex *:data-[slot=select-value]:items-center *:data-[slot=select-value]:gap-2 data-[state=open]:border-sky-700 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        'data-[placeholder]:bg-neutral-50 data-[placeholder]:text-neutral-600',
        'data-[disabled]:bg-neutral-100 data-[disabled]:text-neutral-400',
        isLoading && 'pointer-events-none select-none',
        className,
      )}
      {...props}
    >
      {children}
      <SelectPrimitive.Icon asChild>
        {isLoading ? (
          <Icon name="refresh-ccw" className="size-4 animate-spin opacity-50" />
        ) : (
          <Icon name="chevron-down" className="size-4 opacity-50" />
        )}
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  )
}

function SelectContent({
  className,
  children,
  position = 'popper',
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Content>) {
  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        data-slot="select-content"
        className={cn(
          'bg-popover text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 relative z-50 max-h-(--radix-select-content-available-height) min-w-[8rem] overflow-x-hidden overflow-y-auto rounded-md border shadow-md',
          position === 'popper' &&
            'data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1',
          className,
        )}
        position={position}
        {...props}
      >
        <SelectScrollUpButton />
        <SelectPrimitive.Viewport
          className={cn(
            'p-1',
            position === 'popper' &&
              'h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)] scroll-my-1',
          )}
        >
          {children}
        </SelectPrimitive.Viewport>
        <SelectScrollDownButton />
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  )
}

function SelectLabel({ className, ...props }: React.ComponentProps<typeof SelectPrimitive.Label>) {
  return (
    <SelectPrimitive.Label
      data-slot="select-label"
      className={cn('px-2 py-1.5 text-base font-medium', className)}
      {...props}
    />
  )
}

function SelectItem({ className, children, ...props }: React.ComponentProps<typeof SelectPrimitive.Item>) {
  return (
    <SelectPrimitive.Item
      data-slot="select-item"
      className={cn(
        "focus:bg-accent focus:text-accent-foreground [&_svg:not([class*='text-'])]:text-muted-foreground relative flex w-full cursor-default items-center gap-2 rounded-sm py-1.5 pr-8 pl-2 text-base outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 *:[span]:last:flex *:[span]:last:items-center *:[span]:last:gap-2",
        className,
      )}
      {...props}
    >
      <span className="absolute right-2 flex size-3.5 items-center justify-center">
        <SelectPrimitive.ItemIndicator>
          <CheckIcon className="size-4" />
        </SelectPrimitive.ItemIndicator>
      </span>
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  )
}

function SelectSeparator({ className, ...props }: React.ComponentProps<typeof SelectPrimitive.Separator>) {
  return (
    <SelectPrimitive.Separator
      data-slot="select-separator"
      className={cn('bg-border pointer-events-none -mx-1 my-1 h-px', className)}
      {...props}
    />
  )
}

function SelectScrollUpButton({ className, ...props }: React.ComponentProps<typeof SelectPrimitive.ScrollUpButton>) {
  return (
    <SelectPrimitive.ScrollUpButton
      data-slot="select-scroll-up-button"
      className={cn('flex cursor-default items-center justify-center py-1', className)}
      {...props}
    >
      <ChevronUpIcon className="size-4" />
    </SelectPrimitive.ScrollUpButton>
  )
}

function SelectScrollDownButton({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollDownButton>) {
  return (
    <SelectPrimitive.ScrollDownButton
      data-slot="select-scroll-down-button"
      className={cn('flex cursor-default items-center justify-center py-1', className)}
      {...props}
    >
      <ChevronDownIcon className="size-4" />
    </SelectPrimitive.ScrollDownButton>
  )
}

type Primitive = string | number

type Props<
  TOption,
  TFieldValues extends FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
> = ComponentProps<typeof Select> &
  Omit<UseControllerProps<TFieldValues>, 'control'> & {
    control?: Control<TFieldValues, TContext, TTransformedValues>
    label: string
    options: TOption[]
    itemClassName?: ComponentProps<typeof SelectItem>['className']
    className?: ComponentProps<typeof SelectTrigger>['className']
    placeholder?: string
  }

type TSelectFieldProps<
  TOption,
  TFieldValues extends FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
> = TOption extends Primitive
  ? Props<TOption, TFieldValues, TContext, TTransformedValues> & {
      onValueChange?: (value: string) => void
    }
  : Props<TOption, TFieldValues, TContext, TTransformedValues> & {
      valueKey: keyof TOption
      labelKey: keyof TOption
      onValueChange?: (value: string) => void
    }

const SelectField = <
  TOption extends Primitive | Record<string, unknown>,
  TFieldValues extends FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>({
  control,
  name,
  itemClassName,
  placeholder = 'Selecione',
  options,
  label,
  className,
  onValueChange,
  ...props
}: TSelectFieldProps<TOption, TFieldValues, TContext, TTransformedValues>) => {
  const id = useId()
  return (
    <div>
      <FormField
        control={control}
        name={name}
        render={({ field: { onChange, value } }) => (
          <FormItem>
            <FormLabel id={id}>{label}</FormLabel>
            <FormControl>
              <Select
                {...props}
                onValueChange={(newValue) => {
                  onChange(newValue)
                  onValueChange?.(newValue)
                }}
                value={value}
              >
                <SelectTrigger className={cn('h-11 w-full', className)} aria-label={label} aria-labelledby={id}>
                  <SelectValue placeholder={placeholder} />
                </SelectTrigger>
                <SelectContent>
                  {options.map((option) => {
                    if (isPrimitive(option)) {
                      return (
                        <SelectItem
                          key={option}
                          className={cn('w-full min-w-0 py-3', itemClassName)}
                          value={String(option)}
                        >
                          <span>{option}</span>
                        </SelectItem>
                      )
                    }
                    const hasKeys = typeof option === 'object' && 'valueKey' in props && 'labelKey' in props
                    if (hasKeys) {
                      const _value = String(option[props.valueKey as keyof typeof option])
                      const _label = String(option[props.labelKey as keyof typeof option])
                      return (
                        <SelectItem key={_value} className={cn('py-3', itemClassName)} value={_value}>
                          <span>{_label}</span>
                        </SelectItem>
                      )
                    }
                    return null
                  })}
                </SelectContent>
              </Select>
            </FormControl>
          </FormItem>
        )}
      />
    </div>
  )
}

export { SelectField }

function isPrimitive(value: unknown): value is Primitive {
  return typeof value === 'string' || typeof value === 'number'
}

export {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
}
